import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../utils/supabaseClient';
import Modal from '../../components/Modal';

function highlightMatch(text, searchTerm) {
  if (!searchTerm) return text;
  const regex = new RegExp(`(${searchTerm})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? <mark key={i}>{part}</mark> : part
  );
}

function ViewProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    summary: '',
    company: '',
    author: '',
    domain: '',
    logo: '',
    voice: '',
    audience: '',
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);

  const searchInputRef = useRef(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;

      if (!user) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching profiles:', error);
      }

      setProfiles(data || []);
      setFilteredProfiles(data || []);
      setLoading(false);
    };

    fetchProfiles();
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProfiles(profiles);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredProfiles(
        profiles.filter(
          (profile) =>
            profile.name?.toLowerCase().includes(term) ||
            profile.description?.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, profiles]);

  useEffect(() => {
    const handleProfileCreated = (e) => {
      setProfiles((prev) => [e.detail, ...prev]);
      setFilteredProfiles((prev) => [e.detail, ...prev]);
    };

    const handleProfileDeleted = (e) => {
      const deletedId = e.detail?.id;
      setProfiles((prev) => prev.filter((p) => p.id !== deletedId));
      setFilteredProfiles((prev) => prev.filter((p) => p.id !== deletedId));
    };

    window.addEventListener('profile-created', handleProfileCreated);
    window.addEventListener('profile-deleted', handleProfileDeleted);

    return () => {
      window.removeEventListener('profile-created', handleProfileCreated);
      window.removeEventListener('profile-deleted', handleProfileDeleted);
    };
  }, []);

  const openEditModal = (profile) => {
    setSelectedProfile(profile);
    setForm({
      name: profile.name || '',
      description: profile.description || '',
      summary: profile.summary || '',
      company: profile.company || '',
      author: profile.author || '',
      domain: profile.domain || '',
      logo: profile.logo || '',
      voice: profile.voice || '',
      audience: profile.audience || '',
    });
    setEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleUpdateProfile = async () => {
    if (!selectedProfile?.id) {
      alert('Missing profile ID.');
      return;
    }

    const updatePayload = { ...form };

    const { error } = await supabase
      .from('profiles')
      .update(updatePayload)
      .eq('id', selectedProfile.id);

    if (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile.');
      return;
    }

    const updatedProfiles = profiles.map((p) =>
      p.id === selectedProfile.id ? { ...p, ...updatePayload } : p
    );
    setProfiles(updatedProfiles);
    setFilteredProfiles(updatedProfiles);
    setEditModalOpen(false);
  };

  const confirmDeleteProfile = (profile) => {
    setProfileToDelete(profile);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!profileToDelete?.id) return;

    const { error } = await supabase
      .from('profiles')
      .delete()
      .eq('id', profileToDelete.id);

    if (error) {
      console.error('Error deleting profile:', error);
      alert('Failed to delete profile.');
      return;
    }

    window.dispatchEvent(new CustomEvent('profile-deleted', { detail: profileToDelete }));
    setShowDeleteModal(false);
  };

  if (loading) {
    return (
      <div className="centered-loading">
        <p>Loading profiles...</p>
      </div>
    );
  }

  return (
    <>
      <header>
        <div className="section-title">
          <h1>Profiles</h1>
        </div>
        <div className="breadcrumbs">
          <p>Profiles <i className="bi bi-chevron-right"></i> View All</p>
        </div>
      </header>

      <section className="scroll">
        <div className="contain">
          {profiles.length > 0 && (
            <>
              <div className="view-search">
                <input
                  type="text"
                  id="view-search"
                  ref={searchInputRef}
                  placeholder="Search profiles"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  className="btn-unset"
                  type="button"
                  onClick={() => searchInputRef.current?.focus()}
                >
                  <i className="bi bi-search"></i>
                </button>
              </div>
              {searchTerm.trim() && filteredProfiles.length === 0 && (
                <p className="no-results-message">
                  No results found for "<strong>{searchTerm}</strong>"
                </p>
              )}
            </>
          )}

          <div className="view-rows">
            {filteredProfiles.length === 0 && !searchTerm.trim() ? (
              <div className="no-content centered-loading">
                <button
                  type="button"
                  className="btn-outlined"
                  onClick={() =>
                    window.dispatchEvent(new Event('open-profile-modal'))
                  }
                >
                  Create your first profile
                </button>
              </div>
            ) : (
              filteredProfiles.map((profile) => (
                <div className="view-row profile-row" key={profile.id}>
                  <div>
                    <h2>{highlightMatch(profile.name, searchTerm)}</h2>
                    <p>{highlightMatch(profile.description, searchTerm)}</p>
                  </div>
                  <div className="view-row-actions">
                    <button
                      className="btn-outlined btn-delete"
                      onClick={() => confirmDeleteProfile(profile)}
                    >
                      Delete
                    </button>
                    <button
                      className="btn-outlined"
                      onClick={() => openEditModal(profile)}
                    >
                      Edit
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {editModalOpen && (
        <Modal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          title="Edit Profile"
          actions={[
            {
              label: 'Update',
              className: 'btn-outlined',
              onClick: handleUpdateProfile,
            },
            {
              label: 'Cancel',
              className: 'btn-outlined',
              onClick: () => setEditModalOpen(false),
            },
          ]}
        >
          <form className="create-item">
            <label>Profile Name:</label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleEditChange}
            />

            <label>Description:</label>
            <input
              type="text"
              name="description"
              value={form.description}
              onChange={handleEditChange}
            />

            <label>Domain:</label>
            <input
              type="text"
              name="domain"
              value={form.domain}
              onChange={handleEditChange}
            />

            <div className="row">
              <div>
                <label>Website Summary:</label>
              </div>
            </div>
            <textarea
              name="summary"
              value={form.summary}
              onChange={handleEditChange}
              rows={4}
            />

            <label>Company/Brand:</label>
            <input
              type="text"
              name="company"
              value={form.company}
              onChange={handleEditChange}
            />
            <label>Author:</label>
            <input
              type="text"
              name="author"
              value={form.author}
              onChange={handleEditChange}
            />
            <label>Logo URL:</label>
            <input
              type="text"
              name="logo"
              value={form.logo}
              onChange={handleEditChange}
            />
            <label>Voice:</label>
            <input
              type="text"
              name="voice"
              value={form.voice}
              onChange={handleEditChange}
            />
            <label>Audience:</label>
            <input
              type="text"
              name="audience"
              value={form.audience}
              onChange={handleEditChange}
            />
          </form>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Confirm Delete"
          actions={[
            {
              label: 'Cancel',
              className: 'btn-outlined',
              onClick: () => setShowDeleteModal(false),
            },
            {
              label: 'Delete',
              className: 'btn-outlined btn-delete',
              onClick: handleDelete,
            },
          ]}
        >
          <p>
            Are you sure you want to delete{' '}
            <strong>{profileToDelete?.name}</strong>? This action cannot be undone.
          </p>
        </Modal>
      )}
    </>
  );
}

export default ViewProfiles;